import { FontFamily } from "@redotech/redo-content-builder-common/element-schema";
import { ExtendedAddress } from "@redotech/redo-fulfillment-common/address";
import { FulfillmentOrder } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { PageOrientation } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import { Weight } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { UtmParameters } from "@redotech/redo-marketing-core-common/utm";
import type { FulfillmentDocumentTeam } from "../team-contracts";
import { PackingSlip, PackingSlipPageLayout } from "./packing-slip-definition";

export interface FulfillmentGroupForPackingSlip {
  fulfillmentGroupId: string;
  brandName: string;
  recipientFirstName: string;
  recipientFullName: string;
  orderItems: OrderItemTableColumnData[];
  destinationAddress: ExtendedAddress;
  originAddress: ExtendedAddress;
  billingAddress: ExtendedAddress;
  trackingNumber: string | null;
  recipientPhone: string;
  recipientEmail: string;
  orderNumber: string;
  orderNumberWithoutMarketplaceIdentifier: string;
  secondaryOrderName: string;
  orderDate: string;
  fulfillmentDate: string;
  printedDate: string;
  packageWeight: Weight;
  packageInformation: { name: string; dimensions: string[] }[];
  // Per-box context. Populated when the slip renders for a specific parcel
  // (see PackingSlipInfo.currentParcelId). Empty strings when the slip is a
  // single FG-level slip — keeps TEXT block `{{ variable }}` substitution
  // tidy so unused variables render blank rather than "undefined".
  warehouseName: string;
  boxNumber: string;
  totalBoxes: string;
  boxLabel: string;
  boxBarcodeValue: string;
  productsPrice: string;
  shippingPrice: string;
  tax: string;
  orderTotal: string;
  totalDiscountedPrice: string;
  totalDiscounts: string | undefined;
  discountCodes: string[] | undefined;
  combinedDiscountCodes: string;
  paymentMethod: string | undefined;
  lastFourCardNumber: string | undefined;
  shippingCarrier: string;
  shippingService: string;
  shippingMethod: string;
  requestedService: string;
  noteToCustomer: string;
  noteFromCustomer: string;
  salesChannel: string;
  totalQuantity: number;
  orderForReturnsPortal: FulfillmentOrder | undefined;
  fulfilledFromMultipleLocationsText: string;
  orderTags: string;
  customerId: string;
  customerNumOrders: string;
  customDate1: string;
  customDate2: string;
  customDate3: string;
  customString1: string;
  customString2: string;
  customString3: string;
  customNumber1: string;
  customNumber2: string;
  customNumber3: string;
  customAttributeDate1: string;
  customAttributeDate2: string;
  customAttributeDate3: string;
  customAttributeString1: string;
  customAttributeString2: string;
  customAttributeString3: string;
  customAttributeNumber1: string;
  customAttributeNumber2: string;
  customAttributeNumber3: string;
  shipmentTags: string;
  internalNote: string;
  marketplaceInternalNote: string;
}

export namespace PaddingSize {
  export const SMALL = 8;
  export const MEDIUM = 16;
  export const LARGE = 32;
}

export enum PackingSlipFontWeight {
  NORMAL = "normal",
  BOLD = "bold",
}

export interface Padding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export enum Alignment {
  LEFT = "left",
  CENTER = "center",
  RIGHT = "right",
}

export enum AlignItems {
  LEFT = "Left",
  CENTER = "Center",
  RIGHT = "Right",
}

export const AlignItemsDisplayToCssValue: Record<AlignItems, string> = {
  [AlignItems.LEFT]: "flex-start",
  [AlignItems.CENTER]: "center",
  [AlignItems.RIGHT]: "flex-end",
};

export const FONT_WEIGHTS = [
  PackingSlipFontWeight.NORMAL,
  PackingSlipFontWeight.BOLD,
] as const;

export const FONT_FAMILIES = [
  FontFamily.ARIAL,
  FontFamily.COURIER_NEW,
  FontFamily.GEORGIA,
  FontFamily.LUCIDA_SANS_UNICODE,
  FontFamily.TAHOMA,
  FontFamily.TIMES_NEW_ROMAN,
  FontFamily.TREBUCHET_MS,
  FontFamily.VERDANA,
] as const;

export const RICH_TEXT_VARIABLES: (keyof FulfillmentGroupForPackingSlip)[] = [
  "brandName",
  "recipientFirstName",
  "recipientFullName",
  "orderNumber",
  "secondaryOrderName",
  "orderDate",
  "fulfillmentDate",
  "printedDate",
  "orderTotal",
  "requestedService",
  "shippingService",
  "shippingMethod",
  "salesChannel",
  "totalQuantity",
  "fulfilledFromMultipleLocationsText",
  "orderTags",
  "shipmentTags",
  "internalNote",
  "customerId",
  "customerNumOrders",
  "combinedDiscountCodes",
  "customDate1",
  "customDate2",
  "customDate3",
  "customString1",
  "customString2",
  "customString3",
  "customNumber1",
  "customNumber2",
  "customNumber3",
  "customAttributeDate1",
  "customAttributeDate2",
  "customAttributeDate3",
  "customAttributeString1",
  "customAttributeString2",
  "customAttributeString3",
  "customAttributeNumber1",
  "customAttributeNumber2",
  "customAttributeNumber3",
  "warehouseName",
  "boxNumber",
  "totalBoxes",
  "boxLabel",
  "boxBarcodeValue",
];

export type RichTextVariable = (typeof RICH_TEXT_VARIABLES)[number];
export type RichTextVariables = { [K in RichTextVariable]?: string };

export enum OrderItemTableColumnType {
  BIN = "bin",
  ITEM_THUMBNAIL = "itemThumbnail",
  ITEM_NAME_AND_DESCRIPTION = "itemDescription",
  QUANTITY = "quantity",
  UNIT_PRICE = "unitPrice",
  PRICE = "price",
  SKU = "sku",
  UPC = "upc",
  VENDOR = "vendor",
  CUSTOM_FIELD = "customField",
}

export enum OrderItemSort {
  ORDER_NUMBER = "order-number",
  ORDER_SKU = OrderItemTableColumnType.SKU,
  ORDER_UPC = OrderItemTableColumnType.UPC,
  ORDER_BIN = OrderItemTableColumnType.BIN,
  ORDER_ITEM_NAME = OrderItemTableColumnType.ITEM_NAME_AND_DESCRIPTION,
  ORDER_QUANTITY = OrderItemTableColumnType.QUANTITY,
  CUSTOM = OrderItemTableColumnType.CUSTOM_FIELD,
}

export enum PackingSlipSortDirection {
  ASC = "Ascending",
  DESC = "Descending",
}

/**
 * How bill-of-materials / bundle products are displayed in the order-items
 * table. Applies uniformly to Redo BOM and BigCommerce bundles.
 */
export enum BomDisplayMode {
  /**
   * Legacy behavior (also the meaning of an unset value): Redo BOM items are
   * expanded to their input items; BigCommerce bundles show every row the
   * platform sent (bundle parent and children). The only mode compatible with
   * splitItemsToSingleQuantity.
   */
  DEFAULT = "default",
  /** Only output products: BOMs are not expanded, BC bundle children are hidden. */
  OUTPUT_ITEMS = "output-items",
  /** Output products as rows with their input products nested beneath them. */
  ORGANIZED = "organized",
}

/**
 * The team-level "always hide input items" master setting (
 * `outboundLabels.billOfMaterials.alwaysHideInputItems`) forces output-items
 * display regardless of the per-slip selection.
 */
export function resolveBomDisplayMode(
  alwaysHideInputItems: boolean,
  sectionBomDisplay: BomDisplayMode | null | undefined,
): BomDisplayMode {
  if (alwaysHideInputItems) {
    return BomDisplayMode.OUTPUT_ITEMS;
  }
  return sectionBomDisplay ?? BomDisplayMode.DEFAULT;
}

export enum QrCodeSize {
  EXTRA_SMALL = "Extra Small",
  SMALL = "Small",
  MEDIUM = "Medium",
  LARGE = "Large",
}

export const QrCodeSizeToPixels: Record<QrCodeSize, number> = {
  [QrCodeSize.EXTRA_SMALL]: 75,
  [QrCodeSize.SMALL]: 100,
  [QrCodeSize.MEDIUM]: 150,
  [QrCodeSize.LARGE]: 200,
};

export const OrderItemSortToColumnType: Record<
  OrderItemSort,
  keyof OrderItemTableColumnData
> = {
  [OrderItemSort.ORDER_NUMBER]:
    OrderItemTableColumnType.ITEM_NAME_AND_DESCRIPTION,
  [OrderItemSort.ORDER_SKU]: OrderItemTableColumnType.SKU,
  [OrderItemSort.ORDER_UPC]: OrderItemTableColumnType.UPC,
  [OrderItemSort.ORDER_BIN]: OrderItemTableColumnType.BIN,
  // For backwards compatibility this must exist, but we point it to itemName
  [OrderItemSort.ORDER_ITEM_NAME]: "itemName",
  [OrderItemSort.ORDER_QUANTITY]: OrderItemTableColumnType.QUANTITY,
  [OrderItemSort.CUSTOM]: OrderItemTableColumnType.CUSTOM_FIELD, // Handled specially
};

export interface OrderItemTableColumn {
  type: OrderItemTableColumnType;
  enabled: boolean;
  id?: string | null;
  title?: string | null;
  relativeFontSize?: number | null;
  fontWeight?: PackingSlipFontWeight | null;
  alignment?: Alignment | null;
}

export interface OrderItemTableColumnData {
  bin: string;
  itemThumbnail: string;
  itemName: string;
  itemProductTitle: string;
  itemDescription: string;
  quantity: number;
  unitPrice: string;
  price: string;
  sku: string;
  upc: string;
  vendor: string;
  customField: string;
  selectedOptions: { name: string; value: string }[];
  customAttributes: { key: string; value: string }[];
  metafields: { key: string; value: string }[];
  // BOM/bundle input rows rendered as a nested kit-component table beneath
  // this output product when bomDisplay is ORGANIZED. Children are never
  // nested further (max one level; qty-0 intermediate items are filtered out
  // upstream).
  childItems?: OrderItemTableColumnData[];
}

export const orderItemTableColumnTypeToLabel: Record<
  OrderItemTableColumnType,
  string
> = {
  [OrderItemTableColumnType.BIN]: "Bin",
  [OrderItemTableColumnType.ITEM_THUMBNAIL]: "Image",
  [OrderItemTableColumnType.ITEM_NAME_AND_DESCRIPTION]: "Product name",
  [OrderItemTableColumnType.QUANTITY]: "Quantity",
  [OrderItemTableColumnType.UNIT_PRICE]: "Unit price",
  [OrderItemTableColumnType.PRICE]: "Total price",
  [OrderItemTableColumnType.SKU]: "SKU",
  [OrderItemTableColumnType.UPC]: "UPC",
  [OrderItemTableColumnType.CUSTOM_FIELD]: "Custom field",
  [OrderItemTableColumnType.VENDOR]: "Vendor",
};

export enum PackingSlipBlockType {
  HEADER_MARKER = "header-marker",
  IMAGE = "image",
  LOGO = "logo",
  TEXT = "text",
  SPACER = "spacer",
  FILL_SPACER = "fill-spacer",
  LINE = "line",
  COLUMN = "column",
  BILLING_ADDRESS = "billing-address",
  COMPANY_ADDRESS = "company-address",
  BRAND_NAME = "brand-name",
  CONTACT_INFORMATION = "contact-information",
  NOTE_FROM_CUSTOMER = "note-from-customer",
  NOTE_TO_CUSTOMER = "note-to-customer",
  ORDER_BARCODE = "order-barcode",
  ORDER_DATE = "order-date",
  ORDER_ITEMS = "order-items",
  PRINTED_DATE = "printed-date",
  ORDER_DETAILS = "order-details",
  ORDER_NUMBER = "order-number",
  PACKAGE_INFORMATION = "package-information",
  //OUT_OF_STOCK_ITEMS = "out-of-stock-items", need more definition
  PAYMENT_BREAKDOWN = "payment-breakdown",
  PAYMENT_METHOD = "payment-method",
  //PURCHASE_ORDER_NUMBER = "purchase-order-number", need more definition
  PACKAGE_WEIGHT = "package-weight",
  //PROMOTIONAL_MESSAGE = "promotional-message", can this not be a text block?
  SHIPPING_ADDRESS = "shipping-address",
  SHIPPING_METHOD = "shipping-method",
  REQUESTED_SHIPPING_METHOD = "requested-shipping-method",
  //SPECIAL_INSTRUCTIONS = "special-instructions", can this not be a text block?
  TRACKING_NUMBER = "tracking-number",
  QR_CODE = "qr-code",
  WEATHER_FORECAST = "weather-forecast",
  INTERNAL_NOTES = "internal-notes",
}

export enum PackingSlipBlockCategory {
  GENERAL = "general",
  ORDER = "order",
  MERCHANT = "merchant",
  CUSTOMER = "customer",
}

export namespace Section {
  export interface BaseSection {
    blockId: string;
    duplicate?: () => Section;
    sectionPadding: Padding;
    sectionColor?: string | null;
    alignSelf?: AlignItems | null;
  }

  export interface FontSection extends BaseSection {
    fontFamily?: string | null;
    fontSize?: number | null;
    textColor?: string | null;
    fontWeight?: PackingSlipFontWeight | null;
  }

  export interface AddressSection extends FontSection {
    header?: string | null;
    showHeader?: boolean | null;
    showName?: boolean | null;
    boldName?: boolean | null;
    showCompany?: boolean | null;
    showAddress?: boolean | null;
    showPhone?: boolean | null;
    showEmail?: boolean | null;
  }

  export interface BillingAddress extends AddressSection {
    type: PackingSlipBlockType.BILLING_ADDRESS;
  }

  export interface HeaderMarker extends BaseSection {
    type: PackingSlipBlockType.HEADER_MARKER;
  }
  export interface Column extends BaseSection {
    type: PackingSlipBlockType.COLUMN;
    // Filling a later column first can leave holes in the in-memory array.
    columns: (SectionWithoutColumn | null | undefined)[];
    columnCount: number;
    gap: number;
  }

  export interface CompanyAddress extends AddressSection {
    type: PackingSlipBlockType.COMPANY_ADDRESS;
  }

  export interface ContactInformation extends FontSection {
    type: PackingSlipBlockType.CONTACT_INFORMATION;
    showName: boolean;
    showCompany?: boolean | null;
    showAddress: boolean;
    showPhone: boolean;
    showEmail: boolean;
  }

  export interface NoteFromCustomer extends FontSection {
    type: PackingSlipBlockType.NOTE_FROM_CUSTOMER;
  }

  export interface NoteToCustomer extends FontSection {
    type: PackingSlipBlockType.NOTE_TO_CUSTOMER;
  }

  export interface OrderBarcode extends BaseSection {
    type: PackingSlipBlockType.ORDER_BARCODE;
    width?: number | null;
    height?: number | null;
    useOrderNumber?: boolean | null;
    useSecondaryOrderNumber?: boolean | null;
    useCustomerName?: boolean | null;
    useCustomerId?: boolean | null;
    // When true, encodes the per-box identifier (`<fgId>-<boxNumber>`) on
    // consolidation / per-parcel slips. Falls back to the FG-id barcode when
    // box context is missing — so a stray box-barcode block on a non-parcel
    // slip still scans instead of rendering an empty barcode.
    useBoxBarcode?: boolean | null;
    showAnnotation?: boolean | null;
    removeMarketplaceIdentifier?: boolean | null; // removes BC-, EBAY-, CS- etc. Will not work within redo
  }

  export interface BrandName extends FontSection {
    type: PackingSlipBlockType.BRAND_NAME;
  }

  export interface OrderDate extends FontSection {
    type: PackingSlipBlockType.ORDER_DATE;
  }

  export interface PrintedDate extends FontSection {
    type: PackingSlipBlockType.PRINTED_DATE;
    showPrefix?: boolean | null;
  }

  export interface OrderItems extends FontSection {
    type: PackingSlipBlockType.ORDER_ITEMS;
    sort: OrderItemSort;
    sortDirection?: PackingSlipSortDirection | null;
    customSortField?: string | null; // When sort is CUSTOM, this specifies which custom field
    tableColumns: OrderItemTableColumn[];
    includeMetafields?: boolean | null;
    emphasizeQuantity?: boolean | null;
    rowHeight?: number | null;
    showDescription?: boolean | null;
    includeHeader?: boolean | null;
    tableBorderColor?: string | null;
    tableBorderWidth?: number | null;
    showVerticalColumnLines?: boolean | null;
    customAttributes?: string[] | null;
    showAllCustomAttributes?: boolean | null;
    separateAttributesVertically?: boolean | null;
    makeAttributeNamesBold?: boolean | null;
    splitItemsToSingleQuantity?: boolean | null;
    showOnlyProductTitle?: boolean | null;
    bomDisplay?: BomDisplayMode | null;
  }

  export interface OrderDetails extends FontSection {
    type: PackingSlipBlockType.ORDER_DETAILS;
    showRecipientInformation: boolean;
    showOrderNumber: boolean;
    showOrderDate: boolean;
    showShippingService: boolean;
    stacked?: boolean | null;
  }

  export interface OrderNumber extends FontSection {
    type: PackingSlipBlockType.ORDER_NUMBER;
  }

  export interface PackageInformation extends FontSection {
    type: PackingSlipBlockType.PACKAGE_INFORMATION;
    showHeader?: boolean | null;
    showName?: boolean | null;
    showDimensions?: boolean | null;
  }

  export interface PaymentBreakdown extends FontSection {
    type: PackingSlipBlockType.PAYMENT_BREAKDOWN;
  }

  export interface PaymentMethod extends FontSection {
    type: PackingSlipBlockType.PAYMENT_METHOD;
    showSubtotal: boolean;
    showPaymentMethod: boolean;
    showPaymentDate: boolean;
  }

  export interface PackageWeight extends FontSection {
    type: PackingSlipBlockType.PACKAGE_WEIGHT;
  }

  export interface ShippingAddress extends AddressSection {
    type: PackingSlipBlockType.SHIPPING_ADDRESS;
  }

  export interface RequestedShippingMethod extends FontSection {
    type: PackingSlipBlockType.REQUESTED_SHIPPING_METHOD;
  }

  export interface ShippingMethod extends FontSection {
    type: PackingSlipBlockType.SHIPPING_METHOD;
  }

  export interface TrackingNumber extends FontSection {
    type: PackingSlipBlockType.TRACKING_NUMBER;
  }

  export interface QrCode extends BaseSection {
    type: PackingSlipBlockType.QR_CODE;
    url: string;
    size: QrCodeSize;
    useReturnsPortalUrl: boolean;
  }

  export interface Text extends BaseSection {
    type: PackingSlipBlockType.TEXT;
    textColor: string;
    fontSize: number;
    fontFamily: string;
    linkColor: string;
    text: string;
  }

  export interface Line extends BaseSection {
    type: PackingSlipBlockType.LINE;
    color: string;
  }

  export interface Spacer extends BaseSection {
    type: PackingSlipBlockType.SPACER;
    height: number;
  }

  // Like SwiftUI's `Spacer(minLength:)`: takes at least `minHeight` and
  // otherwise grows to fill the page's remaining vertical space, pushing the
  // content after it to the bottom. Multiple fill spacers on a page split the
  // leftover space evenly. See fill-spacer-dom.ts for the renderer/paginator
  // contract.
  export interface FillSpacer extends BaseSection {
    type: PackingSlipBlockType.FILL_SPACER;
    minHeight: number;
  }

  export interface Image extends BaseSection {
    type: PackingSlipBlockType.IMAGE;
    imageUrl: string;
    showCaption: boolean;
    maxHeight?: number | null;
    caption?: string | null;
    altText?: string | null;
    clickthroughUrl?: string | null;
    aspectRatio?: number | null;
    resolution?: number | null;
  }

  export interface Logo extends BaseSection {
    type: PackingSlipBlockType.LOGO;
    imageUrl?: string | null;
    maxHeight?: number | null;
  }

  // US-only 7-day forecast pulled from api.weather.gov, keyed off the
  // shipping zip. Per-day fields are toggleable so merchants can opt into
  // just temperatures or the full row with icon + condition + precip.
  export interface WeatherForecast extends FontSection {
    type: PackingSlipBlockType.WEATHER_FORECAST;
    showHigh?: boolean | null;
    showLow?: boolean | null;
    showIcon?: boolean | null;
    showShortText?: boolean | null;
    showPrecipitationChance?: boolean | null;
  }

  export interface InternalNotes extends FontSection {
    type: PackingSlipBlockType.INTERNAL_NOTES;
    showHeader?: boolean | null;
  }
}

export type SectionWithoutColumn =
  | Section.Text
  | Section.Line
  | Section.Spacer
  | Section.FillSpacer
  | Section.Image
  | Section.Logo
  | Section.HeaderMarker
  | Section.BillingAddress
  | Section.CompanyAddress
  | Section.BrandName
  | Section.ContactInformation
  | Section.NoteFromCustomer
  | Section.NoteToCustomer
  | Section.OrderBarcode
  | Section.OrderDate
  | Section.PrintedDate
  | Section.OrderItems
  | Section.OrderDetails
  | Section.OrderNumber
  | Section.PackageInformation
  | Section.PaymentBreakdown
  | Section.PaymentMethod
  | Section.PackageWeight
  | Section.RequestedShippingMethod
  | Section.ShippingAddress
  | Section.ShippingMethod
  | Section.TrackingNumber
  | Section.QrCode
  | Section.WeatherForecast
  | Section.InternalNotes;

export type Section = SectionWithoutColumn | Section.Column;

export interface SharedProps {
  team: FulfillmentDocumentTeam;
  template: Omit<PackingSlip, "sections" | "team">;
  isBuilder: boolean;
  blockId?: string;
  utm: UtmParameters;
  onUpdate?: (updater: (section: Section) => Section) => void;
}

export class PackingSlipUtmParameters extends UtmParameters {
  constructor(campaign?: string, contact?: string) {
    super({ source: "redo", medium: "packing-slip", campaign, contact });
  }

  override getSource(): string {
    return super.getSource() ?? "";
  }

  override getMedium(): string {
    return super.getMedium() ?? "";
  }
}

export enum ScreenSize {
  DESKTOP = "desktop",
  MOBILE = "mobile",
}

export enum PageSize {
  FULL_SIZE = "full-size",
  HALF_SIZE = "half-size",
  LABEL = "label",
  CARD_5X7 = "card-5x7",
}

export function getPageFormatDimensions(pageLayout: PackingSlipPageLayout) {
  switch (pageLayout.size) {
    case PageSize.FULL_SIZE: {
      return pageLayout.orientation === PageOrientation.PORTRAIT
        ? { width: 8.5, height: 11 }
        : { width: 11, height: 8.5 };
    }
    case PageSize.HALF_SIZE: {
      return pageLayout.orientation === PageOrientation.PORTRAIT
        ? { width: 5.5, height: 8.5 }
        : { width: 8.5, height: 5.5 };
    }
    case PageSize.LABEL: {
      return pageLayout.orientation === PageOrientation.PORTRAIT
        ? { width: 4, height: 6 }
        : { width: 6, height: 4 };
    }
    case PageSize.CARD_5X7: {
      return pageLayout.orientation === PageOrientation.PORTRAIT
        ? { width: 5, height: 7 }
        : { width: 7, height: 5 };
    }
    // No default
  }
  throw new Error(`Invalid page size: ${pageLayout.size}`);
}
